/**
 * Migration Boundary Registry
 *
 * Classifies every drawer/modal surface in the app by its orchestration
 * status. This is the single source of truth for answering:
 *
 *   "Is this surface managed by the Navigation Continuity System?"
 *
 * Three tiers:
 *   - `orchestrated`:  Fully managed via useOverlay / overlayOrchestrator.
 *                       Local useState(isOpen) is FORBIDDEN.
 *   - `transitioning`: Integration hook exists but not yet wired to the
 *                       production component. Local state coexists temporarily.
 *   - `legacy`:        No integration hook. Local useState controls open/close.
 *                       New patterns still allowed until migration reaches this surface.
 *
 * RULE: No new drawer/modal may be introduced with local boolean open state
 * in a module classified as `orchestrated` or `transitioning`.
 *
 * @module lib/navigation/migrationBoundary
 */

// ── Types ─────────────────────────────────────────────────────────────────

export type MigrationTier = 'orchestrated' | 'transitioning' | 'legacy';

export interface MigrationEntry {
    /** Display-friendly surface name */
    surface: string;
    /** Which tier this surface belongs to */
    tier: MigrationTier;
    /** Surface kind for filtering */
    kind: 'drawer' | 'modal' | 'dialog';
    /** Integration hook (if exists) */
    integrationHook?: string;
    /** File(s) that own the local state today */
    localStateOwners?: string[];
    /** Notes for the migrating developer */
    notes?: string;
}

// ── Registry ──────────────────────────────────────────────────────────────

const registry: MigrationEntry[] = [
    // ────────────────────────────────────────────────────────────────────
    // TRANSITIONING — integration hook exists, not yet wired to production
    // ────────────────────────────────────────────────────────────────────
    {
        surface: 'milestone-drawer',
        tier: 'transitioning',
        kind: 'drawer',
        integrationHook: 'useMilestoneDrawerNavigation',
        localStateOwners: [
            'pages/Milestones.tsx',
            'pages/ProjectDetailRefactored.tsx',
        ],
        notes: 'Compatibility surface only. Canonical milestone edits use the routed governance workspace.',
    },
    {
        surface: 'capital-drawer',
        tier: 'transitioning',
        kind: 'drawer',
        integrationHook: 'useCapitalDrawerNavigation',
        localStateOwners: [
            'components/integrations/CapitalManageDrawer.tsx',
        ],
        notes: 'Nested under milestone-drawer. Wire after milestone is orchestrated.',
    },
    {
        surface: 'evidence-modal',
        tier: 'transitioning',
        kind: 'modal',
        integrationHook: 'useEvidenceModalNavigation',
        localStateOwners: [],
        notes: 'Leaf overlay — no children. Wire after capital-drawer.',
    },
    {
        surface: 'project-detail',
        tier: 'transitioning',
        kind: 'drawer',
        integrationHook: 'useProjectDetailNavigation',
        localStateOwners: [
            'pages/ProjectDetailRefactored.tsx',
        ],
        notes: 'Host page integration. Scroll + session restore ready.',
    },
    {
        surface: 'milestones-tab',
        tier: 'transitioning',
        kind: 'drawer',
        integrationHook: 'useMilestonesTabNavigation',
        localStateOwners: [
            'pages/Milestones.tsx',
        ],
        notes: 'List-page integration with URL-synced deep links.',
    },

    // ────────────────────────────────────────────────────────────────────
    // LEGACY — no integration hook, local useState controls open/close
    // ────────────────────────────────────────────────────────────────────

    // Drawers
    {
        surface: 'documents-filters-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['pages/Documents.tsx'],
    },
    {
        surface: 'documents-preview-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['pages/Documents.tsx'],
    },
    {
        surface: 'company-detail-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['pages/Companies.tsx'],
    },
    {
        surface: 'user-inspector-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['pages/Users.tsx'],
    },
    {
        surface: 'task-inspector-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['pages/Tasks.tsx'],
    },
    {
        surface: 'create-task-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: [
            'pages/Tasks.tsx',
            'pages/ProjectDetailRefactored.tsx',
            'components/ProjectDetailView.tsx',
        ],
    },
    {
        surface: 'create-project-panel',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['pages/Projects.tsx'],
    },
    {
        surface: 'client-decision-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['pages/CompanyProjectOverviewPage.tsx'],
    },
    {
        surface: 'team-member-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['components/ProjectTeamEditor.tsx'],
    },
    {
        surface: 'company-assignment-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['components/ProjectCompaniesSection.tsx'],
    },
    {
        surface: 'policy-preview-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['components/access/PolicyPreviewDrawer.tsx'],
    },
    {
        surface: 'call-event-details-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['features/calls/components/CallEventDetailsDrawer.tsx'],
    },
    {
        surface: 'settlement-detail-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['components/capital/SettlementDetailDrawer.tsx'],
    },
    {
        surface: 'workspace-inspector-drawer',
        tier: 'legacy',
        kind: 'drawer',
        localStateOwners: ['hooks/useWorkspaceLayout.ts'],
    },

    // ────────────────────────────────────────────────────────────────────
    // INVESTOR PORTAL — URL-owned by InvestorDrawerHost.
    // URL ownership stays with `useInvestorPanelState` (?panel=...).
    // The bridge mirrors panel state into the orchestrator stack so
    // back-button, focus restore, and telemetry apply uniformly.
    // ────────────────────────────────────────────────────────────────────
    {
        surface: 'investor-position-detail',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by useInvestorPanelState + InvestorDrawerHost.',
    },
    {
        surface: 'investor-milestone-review',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by useInvestorPanelState + InvestorDrawerHost.',
    },
    {
        surface: 'investor-approval-review',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by InvestorDrawerHost.',
    },
    {
        surface: 'investor-signature-review',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by InvestorDrawerHost.',
    },
    {
        surface: 'investor-gate-review',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by InvestorDrawerHost.',
    },
    {
        surface: 'investor-finance-review',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by InvestorDrawerHost.',
    },
    {
        surface: 'investor-evidence-preview',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by InvestorDrawerHost.',
    },
    {
        surface: 'investor-document-preview',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by InvestorDrawerHost.',
    },
    {
        surface: 'investor-distribution-detail',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/investor/InvestorDrawerHost.tsx'],
        notes: 'URL and rendering owned by InvestorDrawerHost.',
    },

    // ────────────────────────────────────────────────────────────────────
    // CANONICAL DEEP-LINK SURFACES — restorable from ?overlay=<name>.
    // Registered in overlaySurfaceRegistry as canRestore: true.
    // ────────────────────────────────────────────────────────────────────
    {
        surface: 'gate-detail-drawer',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/capital/GateDetailDrawer.tsx'],
        notes: 'Restored from ?overlay=gate-detail-drawer&entityId=<gateId>.',
    },
    {
        surface: 'document-inspector',
        tier: 'transitioning',
        kind: 'drawer',
        localStateOwners: ['components/DocumentInspector.tsx'],
        notes: 'Restored from ?overlay=document-inspector&entityId=<documentId>.',
    },

    // Modals
    {
        surface: 'new-folder-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['pages/Documents.tsx'],
    },
    {
        surface: 'share-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: [
            'pages/Documents.tsx',
        ],
    },
    {
        surface: 'bulk-tag-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['pages/Documents.tsx'],
    },
    {
        surface: 'bulk-link-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['pages/Documents.tsx'],
    },
    {
        surface: 'user-picker-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['pages/Documents.tsx'],
    },
    {
        surface: 'move-document-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['pages/Documents.tsx'],
    },
    {
        surface: 'invite-user-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['pages/Users.tsx'],
    },
    {
        surface: 'create-company-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['pages/Companies.tsx'],
    },
    {
        surface: 'calendar-event-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['features/calendar/components/Calendar.tsx'],
    },
    {
        surface: 'two-factor-setup-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['features/user/components/TwoFactorSetupModal.tsx'],
    },
    {
        surface: 'create-planned-call-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['features/calls/components/CreatePlannedCallModal.tsx'],
    },

    // Dialogs (confirmation / lightweight)
    {
        surface: 'delete-confirm-dialog',
        tier: 'legacy',
        kind: 'dialog',
        localStateOwners: [
            'pages/Users.tsx',
            'components/ProjectDetails.tsx',
            'components/ProjectActionMenu.tsx',
        ],
        notes: 'Generic ConfirmationDialog — migrate last.',
    },
    {
        surface: 'deliverable-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: ['components/MilestoneDeliverables.tsx'],
    },
    {
        surface: 'finance-action-confirm',
        tier: 'legacy',
        kind: 'dialog',
        localStateOwners: ['components/finance/ExecuteFinanceActionButton.tsx'],
    },
    {
        surface: 'period-close-confirm',
        tier: 'legacy',
        kind: 'dialog',
        localStateOwners: ['features/financial-engine/components/PeriodCloseGatePanel.tsx'],
    },
    {
        surface: 'time-reject-modal',
        tier: 'legacy',
        kind: 'modal',
        localStateOwners: [
            'lib/enterprise/components/time-audit/ProjectTimeTable.tsx',
            'lib/enterprise/components/time-audit/AdminTimeTable.tsx',
        ],
    },
];

// ── Public API ────────────────────────────────────────────────────────────

/** Get the full migration registry (read-only). */
export function getMigrationRegistry(): readonly MigrationEntry[] {
    return registry;
}

/** Look up a surface's migration tier. */
export function getMigrationTier(surfaceName: string): MigrationTier | undefined {
    return registry.find((e) => e.surface === surfaceName)?.tier;
}

/** Get all surfaces in a given tier. */
export function getSurfacesByTier(tier: MigrationTier): readonly MigrationEntry[] {
    return registry.filter((e) => e.tier === tier);
}

/** Check whether a surface is safe for orchestrated open/close. */
export function isOrchestrated(surfaceName: string): boolean {
    const tier = getMigrationTier(surfaceName);
    return tier === 'orchestrated';
}

/** Check whether a surface has a usable integration hook. */
export function hasIntegrationHook(surfaceName: string): boolean {
    const entry = registry.find((e) => e.surface === surfaceName);
    return !!entry?.integrationHook;
}

/**
 * Summary stats for dashboards / CI reporting.
 */
export function getMigrationProgress(): {
    total: number;
    orchestrated: number;
    transitioning: number;
    legacy: number;
    percentComplete: number;
} {
    const total = registry.length;
    const orchestrated = registry.filter((e) => e.tier === 'orchestrated').length;
    const transitioning = registry.filter((e) => e.tier === 'transitioning').length;
    const legacy = registry.filter((e) => e.tier === 'legacy').length;
    return {
        total,
        orchestrated,
        transitioning,
        legacy,
        percentComplete: total > 0 ? Math.round((orchestrated / total) * 100) : 0,
    };
}

// ── Enforcement ───────────────────────────────────────────────────────────

let devWarningsEnabled = false;
try {
    devWarningsEnabled = import.meta.env?.DEV === true;
} catch { /* not in Vite */ }

const warnedSurfaces = new Set<string>();

/**
 * Dev-mode enforcement guard. Call when a surface opens via local state
 * (e.g. `useState(isOpen)`) to check if it should be using the orchestrator.
 *
 * Emits a console.warn in dev if the surface is `orchestrated` or `transitioning`.
 * No-ops in production and for legacy surfaces.
 *
 * @example
 * // In a migration-aware wrapper:
 * function openDrawer() {
 *   assertNotOrchestrated('milestone-drawer', 'Milestones.tsx');
 *   setIsOpen(true);
 * }
 */
export function assertNotOrchestrated(
    surfaceName: string,
    callerFile?: string,
): void {
    if (!devWarningsEnabled) return;
    // Only warn once per surface to avoid noise
    if (warnedSurfaces.has(surfaceName)) return;

    const entry = registry.find((e) => e.surface === surfaceName);
    if (!entry) return; // Unknown surface — no enforcement

    if (entry.tier === 'orchestrated') {
        warnedSurfaces.add(surfaceName);
        console.warn(
            `[nav:enforcement] Surface "${surfaceName}" is ORCHESTRATED — ` +
            `local useState(isOpen) is forbidden. ` +
            `Use useOverlay() instead.` +
            (callerFile ? ` (called from ${callerFile})` : ''),
        );
    } else if (entry.tier === 'transitioning') {
        warnedSurfaces.add(surfaceName);
        console.warn(
            `[nav:enforcement] Surface "${surfaceName}" is TRANSITIONING — ` +
            `an integration hook exists (${entry.integrationHook}). ` +
            `Please wire it instead of using local state.` +
            (callerFile ? ` (called from ${callerFile})` : ''),
        );
    }
}

/**
 * Guard for new overlay creation. In dev mode, warns if a new overlay
 * is opened outside the orchestrator for a known surface.
 *
 * Returns true if the surface should use local state (legacy), false
 * if it should use the orchestrator.
 */
export function shouldUseLegacyOpen(surfaceName: string): boolean {
    const tier = getMigrationTier(surfaceName);
    if (!tier) return true; // Unknown — default to legacy-safe
    return tier === 'legacy';
}

// ── Phase C: New surface creation lock-down ───────────────────────────────

const unknownSurfacesWarned = new Set<string>();

/**
 * Dev-mode guard: every surface that passes through the overlay orchestrator
 * MUST be registered in the migration boundary. If a surface name isn't in
 * the registry, this emits a one-time console.warn reminding the developer
 * to add it.
 *
 * Call from the orchestrator's open path (OverlayOrchestratorContext).
 *
 * @returns true if the surface is registered, false otherwise
 */
export function requireRegistration(surfaceName: string): boolean {
    const entry = registry.find((e) => e.surface === surfaceName);
    if (entry) return true;

    if (devWarningsEnabled && !unknownSurfacesWarned.has(surfaceName)) {
        unknownSurfacesWarned.add(surfaceName);
        console.warn(
            `[nav:registration] Surface "${surfaceName}" is not in the migration registry. ` +
            `Add an entry to SURFACE_MIGRATION_REGISTRY in migrationBoundary.ts ` +
            `before shipping. New surfaces must be registered to enable rollout tracking.`,
        );
    }
    return false;
}
